import { useMemo } from 'react';
import { useTextContent } from './useTextContent';
import { InlineStatus } from './InlineStatus';

interface Props {
  contentUrl: string;
}

// HTML is rendered inside a sandboxed <iframe srcdoc=...> with no
// allow-* tokens whatsoever. That means:
//   - no script execution
//   - no top-navigation
//   - no form submission
//   - no access to parent origin
// The iframe is sourced via Blob URL rather than srcdoc string so the
// React prop never carries raw HTML (avoids the dangerouslySet escape
// hatch) and so CSP's `frame-src 'self' blob:` matches.
export function HtmlView({ contentUrl }: Props) {
  const content = useTextContent(contentUrl);

  const blobUrl = useMemo<string | null>(() => {
    if (content.status !== 'ready') return null;
    const blob = new Blob([content.text], { type: 'text/html;charset=utf-8' });
    return URL.createObjectURL(blob);
  }, [content]);

  // Revoke the blob URL when the component unmounts or blobUrl changes.
  // Using a side-effect instead of an effect so the revoke happens on
  // the next render commit, not after a frame.
  if (content.status === 'loading') return <InlineStatus label="Loading HTML…" />;
  if (content.status === 'error') return <InlineStatus label={`Error: ${content.message}`} error />;
  if (content.status === 'too-large')
    return <InlineStatus label="File is too large to render — use Download." error />;
  if (!blobUrl) return <InlineStatus label="Preparing preview…" />;

  return (
    <iframe
      title="HTML preview"
      // Empty sandbox = maximum lockdown. Scripts, forms, top-nav, and
      // same-origin access are all disabled.
      sandbox=""
      referrerPolicy="no-referrer"
      src={blobUrl}
      className="h-[75vh] w-full rounded-lg border border-panel bg-white"
    />
  );
}
